import { useState, useEffect, useRef } from "react";
import { BookOpen, Lock, Unlock, Send, LogOut, TrendingUp, BarChart3, Users, ChevronRight, ShoppingCart, Info, Gift, Copy, Check, Shield } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./components/ui/dialog";
import { TickerTape } from "./components/TradingViewWidgets";
import CoursePage from "./CoursePage";
import AdminDashboard from "./AdminDashboard";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TELEGRAM_URL = "https://t.me/alvo13_official";
const courseIcons = [TrendingUp, BarChart3, Users];
const PACKAGE_IDS = ["technical_analysis", "investment_base", "full_mentor"];

export default function Dashboard({ session, onLogout, translations, activeLang, setActiveLang }) {
  const t = translations[activeLang];
  const [userProfile, setUserProfile] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [activeCourse, setActiveCourse] = useState(null);
  const [showAdmin, setShowAdmin] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [rewardOpen, setRewardOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [checkoutModal, setCheckoutModal] = useState(null); // index of course for pre-checkout modal
  const [refCodeInput, setRefCodeInput] = useState("");
  const [payoutLoading, setPayoutLoading] = useState(false);
  const coursesRef = useRef(null);

  const userName = session?.user?.user_metadata?.full_name
    || session?.user?.user_metadata?.name
    || session?.user?.email?.split("@")[0]
    || "Student";

  // Fetch user profile to check plan status
  const fetchProfile = async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setUserProfile(data);
      }
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    if (session?.access_token) fetchProfile();
  }, [session]);

  const handleCheckout = async (index, referralCode) => {
    setCheckoutLoading(index);
    try {
      const body = {
        package_id: PACKAGE_IDS[index],
        origin_url: window.location.origin,
      };
      if (referralCode && referralCode.trim()) {
        body.referral_code = referralCode.trim();
      }
      const res = await fetch(`${API_URL}/api/checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (e) { /* silent */ }
    setCheckoutLoading(null);
  };

  const handleProceedToPayment = () => {
    const index = checkoutModal;
    setCheckoutModal(null);
    handleCheckout(index, refCodeInput);
    setRefCodeInput("");
  };

  const handleRequestPayout = async () => {
    setPayoutLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/referral/request-payout`, {
        method: "POST",
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (res.ok) {
        fetchProfile();
      }
    } catch (e) { /* silent */ }
    setPayoutLoading(false);
  };

  // Returns "unlocked" | "buyable" | "blocked"
  const getCourseState = (index) => {
    if (!userProfile || userProfile.plan_status !== "paid") return "buyable";
    const tier = userProfile.plan_tier;
    // Full Mentor: only course 2 is unlocked, the other two are blocked
    if (tier === "full_mentor") return index === 2 ? "unlocked" : "blocked";
    // Investment Base: unlocks only itself (index 1)
    if (tier === "investment_base") return index === 1 ? "unlocked" : "buyable";
    // Technical Analysis: unlocks only itself (index 0)
    if (tier === "technical_analysis") return index === 0 ? "unlocked" : "buyable";
    return "buyable";
  };

  const dashT = {
    EN: {
      welcome: "Welcome back,",
      courses: "Your Courses",
      coursesDesc: "Explore our trading programs",
      freeMaterials: "Free Materials",
      freeMaterialsDesc: "Download our educational guides",
      download: "Read Now",
      locked: "Locked",
      unlocked: "Unlocked",
      blocked: "Included in Full Mentor",
      buyNow: "Buy Now",
      aboutCard: "About Our Courses",
      aboutCardDesc: "Learn what makes ALVO 13 mentorship unique",
      rewardCard: "Get Reward",
      rewardCardDesc: "Earn 20% on every recommendation",
      aboutTitle: "About Our Courses",
      aboutIntro: "Every course is unique and strictly individual.",
      aboutSub: "By choosing any of our training programs, you receive full 1-on-1 personal mentorship:",
      aboutPoints: [
        { title: "Risk-Free Practice", text: "We will not only master technical and fundamental analysis but also conduct detailed practice on a demo account." },
        { title: "Error Analysis", text: "A complete review of each of your trades together with your personal mentor." },
        { title: "Convenient Format", text: "All lessons are held online 1-on-1 via any convenient video call application." },
      ],
      aboutFooter: "Learning from the experience of professionals is the best investment you can make before entering real-world trading.",
      aboutCta: "View Courses",
      rewardTitle: "Get Reward",
      rewardHeadline: "Earn 20% on every recommendation!",
      rewardDesc: "Share your unique code with friends. When they enter it during purchase, 20% of the course price is added to your internal balance. You can request a payout anytime.",
      rewardCodeLabel: "Your Referral Code",
      rewardCopy: "Copy",
      rewardCopied: "Copied!",
      rewardBalance: "Your Balance",
      rewardPayout: "Request Payout",
      rewardPayoutRequested: "Payout Requested",
      checkoutTitle: "Proceed to Payment",
      checkoutRefLabel: "Have a friend's referral code?",
      checkoutRefPlaceholder: "e.g. ALVO_A3X7",
      checkoutProceed: "Proceed to Stripe",
      checkoutSkip: "Skip & Pay",
      tiers: [
        { name: "Technical Analysis", price: "350", desc: "Master chart patterns, market structure, and risk management." },
        { name: "Investment Base", price: "600", desc: "Build portfolios with long-term strategy and fundamental analysis." },
        { name: "Full Mentor", price: "900", desc: "1-on-1 mentorship, live trading, and custom strategy development." },
      ],
    },
    RU: {
      welcome: "С возвращением,",
      courses: "Ваши Курсы",
      coursesDesc: "Изучайте наши торговые программы",
      freeMaterials: "Бесплатные Материалы",
      freeMaterialsDesc: "Скачайте наши образовательные руководства",
      download: "Читать",
      locked: "Заблокировано",
      unlocked: "Доступно",
      blocked: "Входит в Полный Менторинг",
      buyNow: "Купить",
      aboutCard: "О курсах",
      aboutCardDesc: "Узнайте, что делает менторство ALVO 13 уникальным",
      rewardCard: "Вознаграждение",
      rewardCardDesc: "Зарабатывайте 20% с каждой рекомендации",
      aboutTitle: "О курсах",
      aboutIntro: "Каждый курс уникален и проходит строго индивидуально.",
      aboutSub: "Выбирая любую из программ обучения, вы получаете полноценное личное менторство:",
      aboutPoints: [
        { title: "Практика без рисков", text: "Мы не только разберем технический и фундаментальный анализ, но и проведем детальную тренировку на демо-счете." },
        { title: "Работа над ошибками", text: "Разбор каждой вашей сделки вместе с личным ментором." },
        { title: "Удобный формат", text: "Все занятия проходят онлайн один на один через любое удобное приложение видеосвязи." },
      ],
      aboutFooter: "Опыт профессионалов — это лучшее, во что можно инвестировать перед началом реальной торговли.",
      aboutCta: "Посмотреть курсы",
      rewardTitle: "Вознаграждение",
      rewardHeadline: "Зарабатывайте 20% с каждой рекомендации!",
      rewardDesc: "Поделитесь своим уникальным кодом с друзьями. Когда они введут его при покупке, 20% стоимости курса добавляется на ваш внутренний баланс. Вы можете запросить выплату в любое время.",
      rewardCodeLabel: "Ваш реферальный код",
      rewardCopy: "Копировать",
      rewardCopied: "Скопировано!",
      rewardBalance: "Ваш баланс",
      rewardPayout: "Запросить выплату",
      rewardPayoutRequested: "Выплата запрошена",
      checkoutTitle: "Перейти к оплате",
      checkoutRefLabel: "Есть реферальный код друга?",
      checkoutRefPlaceholder: "напр. ALVO_A3X7",
      checkoutProceed: "Перейти к Stripe",
      checkoutSkip: "Пропустить и оплатить",
      tiers: [
        { name: "Технический Анализ", price: "350", desc: "Паттерны, структура рынка и управление рисками." },
        { name: "Инвестиционная База", price: "600", desc: "Построение портфеля, долгосрочная стратегия и анализ." },
        { name: "Полный Менторинг", price: "900", desc: "Личное наставничество, живой трейдинг, индивидуальная стратегия." },
      ],
    },
    ES: {
      welcome: "Bienvenido de nuevo,",
      courses: "Tus Cursos",
      coursesDesc: "Explora nuestros programas de trading",
      freeMaterials: "Materiales Gratuitos",
      freeMaterialsDesc: "Descarga nuestras guías educativas",
      download: "Leer Ahora",
      locked: "Bloqueado",
      unlocked: "Desbloqueado",
      blocked: "Incluido en Mentor Completo",
      buyNow: "Comprar",
      aboutCard: "Sobre los cursos",
      aboutCardDesc: "Descubre qué hace única la mentoría de ALVO 13",
      rewardCard: "Recompensa",
      rewardCardDesc: "Gana un 20% por cada recomendación",
      aboutTitle: "Sobre los cursos",
      aboutIntro: "Cada curso es único y estrictamente individual.",
      aboutSub: "Al elegir cualquiera de nuestros programas de formación, recibes una mentoría personal 1 a 1 completa:",
      aboutPoints: [
        { title: "Práctica sin riesgos", text: "No solo dominaremos el análisis técnico y fundamental, sino que también realizaremos prácticas detalladas en una cuenta demo." },
        { title: "Análisis de errores", text: "Una revisión completa de cada una de tus operaciones junto con tu mentor personal." },
        { title: "Formato conveniente", text: "Todas las lecciones se realizan en línea 1 a 1 a través de cualquier aplicación de videollamada conveniente." },
      ],
      aboutFooter: "Aprender de la experiencia de profesionales es la mejor inversión que puedes hacer antes de entrar al trading real.",
      aboutCta: "Ver cursos",
      rewardTitle: "Recompensa",
      rewardHeadline: "¡Gana un 20% por cada recomendación!",
      rewardDesc: "Comparte tu código único con amigos. Cuando lo ingresen durante la compra, el 20% del precio del curso se agrega a tu saldo interno. Puedes solicitar un retiro en cualquier momento.",
      rewardCodeLabel: "Tu código de referido",
      rewardCopy: "Copiar",
      rewardCopied: "¡Copiado!",
      rewardBalance: "Tu saldo",
      rewardPayout: "Solicitar retiro",
      rewardPayoutRequested: "Retiro solicitado",
      checkoutTitle: "Proceder al pago",
      checkoutRefLabel: "¿Tienes el código de referido de un amigo?",
      checkoutRefPlaceholder: "ej. ALVO_A3X7",
      checkoutProceed: "Ir a Stripe",
      checkoutSkip: "Omitir y pagar",
      tiers: [
        { name: "Análisis Técnico", price: "350", desc: "Patrones, estructura del mercado y gestión de riesgos." },
        { name: "Base de Inversión", price: "600", desc: "Construcción de cartera, estrategia a largo plazo y análisis." },
        { name: "Mentor Completo", price: "900", desc: "Mentoría personal, trading en vivo, estrategia personalizada." },
      ],
    },
  };

  const d = dashT[activeLang];

  const referralCode = userProfile?.referral_code || "...";

  const handleCopy = () => {
    navigator.clipboard.writeText(referralCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleViewCourses = () => {
    setAboutOpen(false);
    setTimeout(() => {
      coursesRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
  };

  // Show admin dashboard
  if (showAdmin && userProfile?.is_admin) {
    return (
      <div className="min-h-screen bg-[#050505] text-[#F5F5F7]">
        <nav className="nav-floating" data-testid="dashboard-nav">
          <div className="container-custom nav-inner">
            <div className="logo-text" data-testid="logo">ALVO 13</div>
            <div className="flex items-center gap-4">
              <button className="login-btn" onClick={onLogout} data-testid="logout-button">
                <LogOut className="w-4 h-4" /><span>{t.logout}</span>
              </button>
            </div>
          </div>
        </nav>
        <TickerTape />
        <AdminDashboard session={session} onBack={() => setShowAdmin(false)} />
      </div>
    );
  }

  // Show course page if a course is selected
  if (activeCourse) {
    return (
      <div className="min-h-screen bg-[#050505] text-[#F5F5F7]">
        <nav className="nav-floating" data-testid="dashboard-nav">
          <div className="container-custom nav-inner">
            <div className="logo-text" data-testid="logo">ALVO 13</div>
            <div className="flex items-center gap-4">
              <div className="lang-toggle" data-testid="language-toggle">
                {["RU", "EN", "ES"].map((lang) => (
                  <button key={lang} className={`lang-btn ${activeLang === lang ? "active" : ""}`}
                    onClick={() => setActiveLang(lang)} data-testid={`lang-btn-${lang.toLowerCase()}`}>{lang}</button>
                ))}
              </div>
              <button className="login-btn" onClick={onLogout} data-testid="logout-button">
                <LogOut className="w-4 h-4" /><span>{t.logout}</span>
              </button>
            </div>
          </div>
        </nav>
        <TickerTape />
        <CoursePage
          session={session}
          courseId={activeCourse}
          onBack={() => setActiveCourse(null)}
          activeLang={activeLang}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-[#F5F5F7]">
      {/* Dashboard Nav */}
      <nav className="nav-floating" data-testid="dashboard-nav">
        <div className="container-custom nav-inner">
          <div className="logo-text" data-testid="logo">ALVO 13</div>
          <div className="flex items-center gap-4">
            <div className="lang-toggle" data-testid="language-toggle">
              {["RU", "EN", "ES"].map((lang) => (
                <button
                  key={lang}
                  className={`lang-btn ${activeLang === lang ? "active" : ""}`}
                  onClick={() => setActiveLang(lang)}
                  data-testid={`lang-btn-${lang.toLowerCase()}`}
                >
                  {lang}
                </button>
              ))}
            </div>
            {userProfile?.is_admin && (
              <button className="admin-nav-btn" onClick={() => setShowAdmin(true)} data-testid="admin-panel-btn">
                <Shield className="w-4 h-4" /> Admin
              </button>
            )}
            <button className="login-btn" onClick={onLogout} data-testid="logout-button">
              <LogOut className="w-4 h-4" />
              <span>{t.logout}</span>
            </button>
          </div>
        </div>
      </nav>

      <TickerTape />

      {/* Dashboard Content */}
      <div className="dashboard-content" data-testid="dashboard">
        {/* Welcome */}
        <div className="dash-welcome" data-testid="dash-welcome">
          <h1 className="dash-welcome-title">{d.welcome}</h1>
          <p className="dash-welcome-name">{userName}</p>
        </div>

        {/* Quick Actions */}
        <div className="dash-actions" data-testid="dash-actions">
          <button className="dash-action-card" onClick={() => setAboutOpen(true)} data-testid="about-courses-btn">
            <div className="dash-action-icon about-icon"><Info className="w-5 h-5" /></div>
            <div>
              <h3 className="dash-action-title">{d.aboutCard}</h3>
              <p className="dash-action-desc">{d.aboutCardDesc}</p>
            </div>
            <ChevronRight className="w-4 h-4 dash-action-chevron" />
          </button>
          <button className="dash-action-card" onClick={() => setRewardOpen(true)} data-testid="get-reward-btn">
            <div className="dash-action-icon reward-icon"><Gift className="w-5 h-5" /></div>
            <div>
              <h3 className="dash-action-title">{d.rewardCard}</h3>
              <p className="dash-action-desc">{d.rewardCardDesc}</p>
            </div>
            <ChevronRight className="w-4 h-4 dash-action-chevron" />
          </button>
        </div>

        {/* Course Grid */}
        <section className="dash-section" data-testid="dash-courses" ref={coursesRef}>
          <h2 className="dash-section-title">{d.courses}</h2>
          <p className="dash-section-desc">{d.coursesDesc}</p>
          <div className="dash-grid">
            {d.tiers.map((tier, i) => {
              const Icon = courseIcons[i];
              const state = getCourseState(i);
              const isBlocked = state === "blocked";
              const isUnlocked = state === "unlocked";
              return (
                <div key={i} className={`dash-card ${isUnlocked ? "unlocked" : isBlocked ? "blocked" : "locked"}`} data-testid={`course-card-${i}`}>
                  <div className="dash-card-header">
                    <div className={`dash-card-icon ${isUnlocked ? "free-icon" : ""}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <span className={`dash-card-badge ${isUnlocked ? "free-badge" : isBlocked ? "blocked-badge" : "locked-badge"}`}>
                      {isUnlocked ? <><Unlock className="w-3 h-3" />{d.unlocked}</> : isBlocked ? <><Lock className="w-3 h-3" />{d.blocked}</> : <><Lock className="w-3 h-3" />{d.locked}</>}
                    </span>
                  </div>
                  <h3 className="dash-card-title">{tier.name}</h3>
                  <p className="dash-card-price">{tier.price}€</p>
                  <p className="dash-card-desc">{tier.desc}</p>
                  {isUnlocked ? (
                    <button
                      onClick={() => setActiveCourse(PACKAGE_IDS[i])}
                      className="dash-card-btn free-btn"
                      data-testid={`course-access-btn-${i}`}
                    >
                      {d.download}
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  ) : isBlocked ? (
                    <div className="dash-card-btn blocked-btn" data-testid={`course-blocked-${i}`}>
                      <Lock className="w-4 h-4" />
                      {d.blocked}
                    </div>
                  ) : (
                    <button
                      className="dash-card-btn buy-course-btn"
                      onClick={() => { setCheckoutModal(i); setRefCodeInput(""); }}
                      disabled={checkoutLoading === i}
                      data-testid={`course-buy-btn-${i}`}
                    >
                      <ShoppingCart className="w-4 h-4" />
                      {checkoutLoading === i ? "..." : d.buyNow}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Free Materials */}
        <section className="dash-section" data-testid="dash-materials">
          <h2 className="dash-section-title">{d.freeMaterials}</h2>
          <p className="dash-section-desc">{d.freeMaterialsDesc}</p>
          <div className="dash-grid">
            {t.academy.books.map((book, i) => (
              <div key={i} className="dash-card free" data-testid={`material-card-${i}`}>
                <div className="dash-card-header">
                  <div className="dash-card-icon free-icon">
                    <BookOpen className="w-6 h-6" />
                  </div>
                  <span className="dash-card-badge free-badge">Free</span>
                </div>
                <h3 className="dash-card-title">{book.name}</h3>
                <p className="dash-card-desc">{book.desc}</p>
                <a
                  href={`https://wa.me/351911770730?text=${encodeURIComponent(`Hello! I want to receive the ${book.name} from ALVO 13 Academy.`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="dash-card-btn free-btn"
                  data-testid={`material-btn-${i}`}
                >
                  {d.download}
                  <ChevronRight className="w-4 h-4" />
                </a>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* About Our Courses Modal */}
      <Dialog open={aboutOpen} onOpenChange={setAboutOpen}>
        <DialogContent className="about-modal-content" data-testid="about-courses-modal">
          <DialogHeader>
            <DialogTitle className="about-modal-title">{d.aboutTitle}</DialogTitle>
          </DialogHeader>
          <div className="about-modal-body">
            <p className="about-modal-intro">{d.aboutIntro}</p>
            <p className="about-modal-sub">{d.aboutSub}</p>
            <div className="about-modal-points">
              {d.aboutPoints.map((p, i) => (
                <div key={i} className="about-modal-point" data-testid={`about-point-${i}`}>
                  <div className="about-point-marker">{i + 1}</div>
                  <div>
                    <h4 className="about-point-title">{p.title}</h4>
                    <p className="about-point-text">{p.text}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="about-modal-footer">{d.aboutFooter}</p>
            <button className="about-modal-cta" onClick={handleViewCourses} data-testid="about-view-courses-btn">
              {d.aboutCta} <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Get Reward Modal */}
      <Dialog open={rewardOpen} onOpenChange={setRewardOpen}>
        <DialogContent className="reward-modal-content" data-testid="reward-modal">
          <DialogHeader>
            <DialogTitle className="reward-modal-title">{d.rewardTitle}</DialogTitle>
          </DialogHeader>
          <div className="reward-modal-body">
            <div className="reward-modal-badge">20%</div>
            <h3 className="reward-modal-headline">{d.rewardHeadline}</h3>
            <p className="reward-modal-desc">{d.rewardDesc}</p>
            <div className="reward-link-section">
              <label className="reward-link-label">{d.rewardCodeLabel}</label>
              <div className="reward-link-row">
                <input
                  type="text"
                  readOnly
                  value={referralCode}
                  className="reward-link-input reward-code-input"
                  data-testid="referral-code-input"
                />
                <button className="reward-copy-btn" onClick={handleCopy} data-testid="referral-copy-btn">
                  {copied ? <><Check className="w-4 h-4" /> {d.rewardCopied}</> : <><Copy className="w-4 h-4" /> {d.rewardCopy}</>}
                </button>
              </div>
            </div>
            <div className="reward-balance-section" data-testid="referral-balance-section">
              <div className="reward-balance-row">
                <span className="reward-balance-label">{d.rewardBalance}:</span>
                <span className="reward-balance-value" data-testid="referral-balance-value">{(userProfile?.referral_balance || 0).toFixed(2)} €</span>
              </div>
              {userProfile?.payout_requested ? (
                <div className="reward-payout-requested" data-testid="payout-requested-badge">
                  <Check className="w-4 h-4" /> {d.rewardPayoutRequested}
                </div>
              ) : (
                <button
                  style={{
                    width: "100%", padding: 11, background: "#dc2626", color: "#fff",
                    border: "none", borderRadius: 8, fontWeight: 700, fontSize: "0.88rem",
                    cursor: "pointer", opacity: (payoutLoading || (userProfile?.referral_balance || 0) <= 0) ? 0.4 : 1,
                  }}
                  onClick={handleRequestPayout}
                  disabled={payoutLoading || (userProfile?.referral_balance || 0) <= 0}
                  data-testid="request-payout-btn"
                >
                  {payoutLoading ? "..." : d.rewardPayout}
                </button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pre-Checkout Referral Modal */}
      <Dialog open={checkoutModal !== null} onOpenChange={(open) => { if (!open) setCheckoutModal(null); }}>
        <DialogContent style={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.1)", maxWidth: 420 }} data-testid="checkout-ref-modal">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Manrope', sans-serif", color: "#F5F5F7", fontSize: "1.15rem" }}>{d.checkoutTitle}</DialogTitle>
          </DialogHeader>
          <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 8 }}>
            <p style={{ fontSize: "1.05rem", fontWeight: 700, color: "#22c55e", textAlign: "center", padding: 12, background: "rgba(34,197,94,0.08)", borderRadius: 8, margin: 0 }} data-testid="checkout-course-name">
              {checkoutModal !== null && d.tiers[checkoutModal]?.name} — {checkoutModal !== null && d.tiers[checkoutModal]?.price}€
            </p>
            <label style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.85rem" }}>{d.checkoutRefLabel}</label>
            <input
              type="text"
              placeholder={d.checkoutRefPlaceholder}
              value={refCodeInput}
              onChange={(e) => setRefCodeInput(e.target.value.toUpperCase())}
              style={{
                width: "100%", padding: "13px 16px", background: "#171717",
                border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, color: "#F5F5F7",
                fontFamily: "'SF Mono', 'Fira Code', monospace", fontSize: "1rem",
                letterSpacing: "0.06em", outline: "none", boxSizing: "border-box",
              }}
              onFocus={(e) => { e.target.style.borderColor = "#22c55e"; e.target.style.boxShadow = "0 0 0 3px rgba(34,197,94,0.15), 0 0 12px rgba(34,197,94,0.08)"; }}
              onBlur={(e) => { e.target.style.borderColor = "rgba(255,255,255,0.1)"; e.target.style.boxShadow = "none"; }}
              data-testid="checkout-ref-input"
            />
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={handleProceedToPayment}
                disabled={checkoutLoading !== null}
                style={{
                  flex: 1, padding: 12, background: "#16a34a", color: "#fff",
                  border: "none", borderRadius: 8, fontWeight: 700, fontSize: "0.9rem",
                  cursor: "pointer", opacity: checkoutLoading !== null ? 0.5 : 1,
                }}
                data-testid="checkout-proceed-btn"
              >
                {checkoutLoading !== null ? "..." : d.checkoutProceed}
              </button>
              <button
                onClick={() => { const idx = checkoutModal; setCheckoutModal(null); handleCheckout(idx, ""); setRefCodeInput(""); }}
                disabled={checkoutLoading !== null}
                style={{
                  flex: 1, padding: 12, background: "#dc2626", color: "#fff",
                  border: "none", borderRadius: 8, fontWeight: 700, fontSize: "0.9rem",
                  cursor: "pointer", opacity: checkoutLoading !== null ? 0.5 : 1,
                }}
                data-testid="checkout-skip-btn"
              >
                {d.checkoutSkip}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
